/* eslint-disable no-console */
import { useEffect, useRef, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";

interface WebSocketMessage {
  type: string;
  data?: any;
  [key: string]: any;
}

interface UseWebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  reconnectAttempts?: number;
  reconnectInterval?: number;
}

type ConnectionStatus = "connecting" | "connected" | "disconnected" | "error";

const WS_BASE_URL = import.meta.env.VITE_WS_URL || "ws://localhost:8000/ws";

export const useWebSocket = (endpoint: string | null, options: UseWebSocketOptions = {}) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const ws = useRef<WebSocket | null>(null);
  const reconnectCount = useRef(0);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("disconnected");
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  const {
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    reconnectAttempts = 5,
    reconnectInterval = 3000,
  } = options;

  // Refs so connect() doesn't depend on callbacks — avoids reconnecting on every parent re-render
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);
  onMessageRef.current = onMessage;
  onConnectRef.current = onConnect;
  onDisconnectRef.current = onDisconnect;
  onErrorRef.current = onError;

  const connect = useCallback(() => {
    if (!isAuthenticated || !endpoint) return;

    setConnectionStatus("connecting");

    // Auth via httpOnly access_token cookie on the API host (no query token).
    const url = `${WS_BASE_URL}${endpoint}`;
    ws.current = new WebSocket(url);

    ws.current.onopen = () => {
      console.log(`WebSocket connected: ${endpoint}`);
      setIsConnected(true);
      setConnectionStatus("connected");
      reconnectCount.current = 0;
      onConnectRef.current?.();
    };

    ws.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        setLastMessage(message);
        onMessageRef.current?.(message);
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    };

    ws.current.onclose = () => {
      console.log(`WebSocket disconnected: ${endpoint}`);
      setIsConnected(false);
      setConnectionStatus("disconnected");
      onDisconnectRef.current?.();

      // Attempt to reconnect
      if (reconnectCount.current < reconnectAttempts) {
        reconnectCount.current += 1;
        console.log(`Reconnecting... (attempt ${reconnectCount.current})`);
        setTimeout(connect, reconnectInterval);
      }
    };

    ws.current.onerror = (error) => {
      console.error("WebSocket error:", error);
      setConnectionStatus("error");
      onErrorRef.current?.(error);
    };
  }, [isAuthenticated, endpoint, reconnectAttempts, reconnectInterval]);

  const disconnect = useCallback(() => {
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
  }, []);

  const sendMessage = useCallback((message: any) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    } else {
      console.warn("WebSocket is not connected");
    }
  }, []);

  useEffect(() => {
    if (endpoint) {
      connect();
    }
    return () => disconnect();
  }, [connect, disconnect, endpoint]);

  return {
    isConnected,
    connectionStatus,
    lastMessage,
    sendMessage,
    disconnect,
    reconnect: connect,
  };
};

// Specialized hooks for specific WebSocket channels

export const useStatementWebSocket = (statementId: number, onUpdate: (data: any) => void) => {
  return useWebSocket(`/statements/${statementId}/`, {
    onMessage: (message) => {
      if (message.type === "statement_update") {
        onUpdate(message);
      }
    },
  });
};

export const useBorrowerWebSocket = (borrowerId: number, onUpdate: (data: any) => void) => {
  return useWebSocket(`/borrower/${borrowerId}/`, {
    onMessage: (message) => {
      if (
        message.type === "borrower_update" ||
        message.type === "underwriting_update" ||
        message.type === "progress_update" ||
        message.type === "task_completed" ||
        message.type === "task_error" ||
        message.type === "transaction_enrichment_completed"
      ) {
        onUpdate(message);
      }
    },
  });
};

export const useUnderwritingWebSocket = (borrowerId: number, onUpdate: (data: any) => void) => {
  return useWebSocket(`/underwriting/${borrowerId}/`, {
    onMessage: (message) => {
      if (
        message.type === "progress_update" ||
        message.type === "task_completed" ||
        message.type === "task_error" ||
        message.type === "underwriting_update"
      ) {
        onUpdate(message);
      }
    },
  });
};

export const useDashboardWebSocket = (onUpdate: (data: any) => void) => {
  return useWebSocket("/dashboard/", {
    onMessage: (message) => {
      if (
        message.type === "dashboard_update" ||
        message.type === "notification" ||
        message.type === "batch_refresh_progress"
      ) {
        onUpdate(message);
      }
    },
  });
};

export default useWebSocket;
